import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { stripTrailingSlash } from '@/lib/utils';
import CustomAILogo from '../CustomAILogo';

/* About Page blocked out until redesigned. */
const navLinks = [
  { name: 'Features', path: '/services/' },
  { name: 'Training', path: '/training-workshop/' },
  /*{ name: 'How it Works', path: '/about' },*/
  { name: 'FAQ', path: '/faq/' },
  { name: 'Contact', path: '/contact/' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const currentPath = stripTrailingSlash(location.pathname);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl border-b' : 'bg-transparent border-b border-transparent'
      }`}
      style={scrolled ? {
        backgroundColor: 'var(--c-bg-nav)',
        borderColor: 'var(--c-nav-border)',
      } : {}}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <div data-testid="navbar-logo">
            <CustomAILogo to="/" height={36} />
          </div>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path + link.name}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                className={`text-sm font-medium transition-colors duration-200 no-underline ${
                  currentPath === stripTrailingSlash(link.path)
                    ? 'text-brand-white'
                    : 'text-brand-muted hover:text-brand-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop right: CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/contact/"
              data-testid="navbar-cta"
              className="inline-flex items-center px-5 py-2 rounded-full text-sm font-semibold tracking-wide no-underline transition-all duration-200"
              style={{
                background: 'rgba(var(--c-purple-rgb), 0.10)',
                border: '1px solid rgba(var(--c-purple-rgb), 0.30)',
                color: 'var(--c-purple-400)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(var(--c-purple-rgb), 0.18)';
                e.currentTarget.style.borderColor = 'rgba(var(--c-purple-rgb), 0.55)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(var(--c-purple-rgb), 0.10)';
                e.currentTarget.style.borderColor = 'rgba(var(--c-purple-rgb), 0.30)';
              }}
            >
              Book a Call
            </Link>
            <a
              href="https://platform.custum.ai/"
              data-testid="navbar-login"
              className="inline-flex items-center px-5 py-2 rounded-full text-sm font-semibold tracking-wide no-underline transition-colors duration-200 text-brand-muted hover:text-brand-white"
            >
              Login
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="flex md:hidden text-brand-white p-2"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden backdrop-blur-xl"
            style={{
              backgroundColor: 'var(--c-bg-nav)',
              borderBottom: '1px solid var(--c-border)',
            }}
          >
            <div className="px-6 py-6 flex flex-col gap-4">

              {navLinks.map((link) => (
                <Link
                  key={link.path + link.name}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`text-sm font-medium no-underline py-2 transition-colors duration-200 ${
                    currentPath === stripTrailingSlash(link.path)
                      ? 'text-brand-white'
                      : 'text-brand-muted'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact/"
                className="mt-2 inline-flex items-center justify-center px-6 py-3 rounded-full text-sm font-semibold no-underline transition-all duration-200"
                style={{
                  background: 'rgba(var(--c-purple-rgb), 0.12)',
                  border: '1px solid rgba(var(--c-purple-rgb), 0.32)',
                  color: 'var(--c-purple-400)',
                }}
              >
                Book a Call
              </Link>
              <a
                href="https://platform.custum.ai/"
                className="inline-flex items-center justify-center px-6 py-3 rounded-full text-sm font-semibold no-underline transition-colors duration-200 text-brand-muted"
              >
                Login
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
